import type { EvidenceSourceFilters } from '@heidi/evidence/contracts';
import type { IntegrationPartnerRequest } from '@/utils/timelineEventSource';

export interface EnforcementState {
  isEnforced: boolean | undefined;
  sourceControl: {
    clinicalSources: { enabled: boolean; changeable: boolean };
    library: { enabled: boolean; changeable: boolean };
    webCollections: { enabled: boolean; changeable: boolean };
    integrationPartners: { enabled: boolean; changeable: boolean };
  };
  enforcedLibraryCollectionIds: string[] | null;
  /** EVI-1958: optional IDs members may toggle when per-collection modes are on */
  optionalLibraryCollectionIds?: string[] | null;
  libraryCollectionIdsChangeable: boolean;
  /**
   * Honour leftover library ID lists only while per-collection library
   * enforcement is on (`library.changeable === false`) or the legacy
   * collection-selection lock is on. Callers that go through
   * useTeamEvidenceEnforcement should pass the persisted-field derivation;
   * omitted falls back to library.changeable + libraryCollectionIdsChangeable.
   */
  libraryCollectionIdListsActive?: boolean;
  enforcedWebCollectionIds: string[] | null;
  webCollectionIdsChangeable: boolean;
  isPerCollectionLibraryModesEnabled?: boolean;
}

export interface RawSourceConfig {
  evidenceSourceFilters: EvidenceSourceFilters;
  userDocCollectionIds: string[];
  sourceListIds: string[] | undefined;
  integrationPartners: IntegrationPartnerRequest[] | undefined;
  includeUserDocs: boolean;
  /** Unified collections mode — routed as ``collection_ids`` on the request. */
  collectionIds?: string[];
}

export type SanitizedSourceConfig = RawSourceConfig;

/** 24-char hex = legacy source list ID; unified collection UUIDs use collection_ids. */
const LEGACY_HEX_RE = /^[0-9a-f]{24}$/i;

function unifiedWebCollectionIds(webIds: readonly string[] | null | undefined): string[] {
  return (webIds ?? []).filter((id) => !LEGACY_HEX_RE.test(id));
}

function legacyWebCollectionIds(webIds: readonly string[] | null | undefined): string[] {
  return (webIds ?? []).filter((id) => LEGACY_HEX_RE.test(id));
}

function mergeUniqueCollectionIds(...lists: readonly (readonly string[] | undefined)[]): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const list of lists) {
    for (const id of list ?? []) {
      if (!seen.has(id)) {
        seen.add(id);
        merged.push(id);
      }
    }
  }
  return merged;
}

/**
 * EVI-2179: Merge enforced/optional library IDs with unified web collection IDs
 * for the single collection_ids enforcement path.
 */
function applyUnifiedCollectionIdEnforcement(
  rawIds: readonly string[] | undefined,
  bucketEnabled: boolean,
  pinnedIds: readonly string[],
  optionalIds: readonly string[],
  allowlistIds: readonly string[]
): string[] {
  if (!bucketEnabled) return [];
  if (!pinnedIds.length && !optionalIds.length && !allowlistIds.length) {
    return rawIds ? [...rawIds] : [];
  }

  const allowedClient = new Set([...optionalIds, ...allowlistIds]);
  const merged = [...pinnedIds];
  const seen = new Set(pinnedIds);
  for (const id of rawIds ?? []) {
    if (allowedClient.has(id) && !seen.has(id)) {
      merged.push(id);
      seen.add(id);
    }
  }
  return merged;
}

/**
 * Apply enforcement to a list of selected IDs.
 *
 * - Bucket disabled → empty.
 * - Not changeable + allowlist → pin to allowlist (admin-forced selection).
 * - Changeable + allowlist → intersect user IDs with allowlist (EVI-1368).
 * - Otherwise → pass through.
 *
 * Treats an empty allowlist as "no enforcement" (same contract as
 * `buildAllowedIdSet` in sourceControlSelectors.ts). The upstream hook
 * normalises `[]` → `null`, so this is belt-and-braces for any future
 * direct caller.
 */
function applyListEnforcement(
  rawIds: readonly string[] | undefined,
  bucketEnabled: boolean,
  changeable: boolean,
  enforcedIds: readonly string[] | null
): string[] | undefined {
  if (!bucketEnabled) return undefined;
  // Copy on pass-through so callers can't mutate the original `rawIds` via
  // reference aliasing (the `readonly` modifier is lost once we return).
  if (!enforcedIds || enforcedIds.length === 0) {
    return rawIds ? [...rawIds] : undefined;
  }
  if (!changeable) return [...enforcedIds];
  const allowSet = new Set(enforcedIds);
  return (rawIds ?? []).filter((id) => allowSet.has(id));
}

/**
 * EVI-1958: always include enforced IDs; keep client selections that are optional.
 */
function applyPerCollectionLibraryModes(
  rawIds: readonly string[] | undefined,
  bucketEnabled: boolean,
  enforcedIds: readonly string[] | null | undefined,
  optionalIds: readonly string[] | null | undefined
): string[] | undefined {
  if (!bucketEnabled) return undefined;
  const enforced = enforcedIds ?? [];
  const optional = optionalIds ?? [];
  // Caller should only invoke when at least one list is non-empty. Empty both
  // means "no per-collection config" — treat as no ID filter (fail-open), same
  // as legacy empty allowlist.
  if (enforced.length === 0 && optional.length === 0) {
    return rawIds ? [...rawIds] : undefined;
  }
  const enforcedSet = new Set(enforced);
  const optionalSet = new Set(optional);
  const merged = [...enforced];
  for (const id of rawIds ?? []) {
    if (optionalSet.has(id) && !enforcedSet.has(id)) {
      merged.push(id);
    }
  }
  return merged;
}

/**
 * Honour library ID lists only while per-collection library enforcement is on
 * (`library.changeable === false`) or the legacy collection-selection lock is on.
 * Both true is leftover IDs after that switch was turned off and must fail-open.
 * Per-collection enforcement means each library is enforced or optional — there
 * is no blanket "always search the whole library" pin.
 */
function areLibraryCollectionIdListsActive(
  libraryChangeable: boolean,
  libraryCollectionIdsChangeable: boolean
): boolean {
  return libraryChangeable === false || libraryCollectionIdsChangeable === false;
}

/**
 * Pure function that applies team evidence enforcement to raw source config.
 *
 * Defense-in-depth: the UI already disables toggles via useTeamEvidenceEnforcement,
 * and the BE shadow-checks via validate_evidence_overrides. This layer catches the
 * edge case where stale localStorage (saved before admin turned enforcement on,
 * or before the allowlist was narrowed) re-hydrates Jotai atoms with now-disallowed
 * source preferences.
 *
 * EVI-1368: When enforcement is active, collection/source-list IDs are ALWAYS
 * intersected with the allowlist — regardless of `*_changeable`. `changeable`
 * controls whether the user can pick a subset of the allowlist; it does NOT
 * gate whether enforcement applies. When `!changeable`, we additionally pin to
 * the full allowlist (so a non-changeable selection can never be empty).
 */
export function sanitizeSourceConfig(
  raw: RawSourceConfig,
  enforcement: EnforcementState
): SanitizedSourceConfig {
  if (!enforcement.isEnforced) return raw;

  const {
    sourceControl,
    enforcedLibraryCollectionIds,
    optionalLibraryCollectionIds,
    libraryCollectionIdsChangeable,
    libraryCollectionIdListsActive,
    enforcedWebCollectionIds,
    webCollectionIdsChangeable,
    isPerCollectionLibraryModesEnabled,
  } = enforcement;

  let cleanFilters = [...raw.evidenceSourceFilters];
  if (!sourceControl.clinicalSources.enabled) {
    cleanFilters = cleanFilters.filter(
      (f) => f !== 'local_guidelines' && f !== 'primary_evidence'
    );
  } else if (!sourceControl.clinicalSources.changeable) {
    if (!cleanFilters.includes('local_guidelines')) cleanFilters.push('local_guidelines');
    if (!cleanFilters.includes('primary_evidence')) cleanFilters.push('primary_evidence');
  }

  const libraryIdListsActive =
    libraryCollectionIdListsActive ??
    areLibraryCollectionIdListsActive(
      sourceControl.library.changeable,
      libraryCollectionIdsChangeable
    );
  const ignoreLibraryIdLists =
    Boolean(isPerCollectionLibraryModesEnabled) && !libraryIdListsActive;
  const effectiveEnforcedLibrary = ignoreLibraryIdLists
    ? null
    : enforcedLibraryCollectionIds;
  const effectiveOptionalLibrary = ignoreLibraryIdLists
    ? null
    : optionalLibraryCollectionIds;

  const usePerCollectionModes =
    Boolean(isPerCollectionLibraryModesEnabled) &&
    !ignoreLibraryIdLists &&
    ((effectiveEnforcedLibrary?.length ?? 0) > 0 ||
      (effectiveOptionalLibrary?.length ?? 0) > 0);

  const cleanUserDocCollectionIds = usePerCollectionModes
    ? (applyPerCollectionLibraryModes(
        raw.userDocCollectionIds,
        sourceControl.library.enabled,
        effectiveEnforcedLibrary,
        effectiveOptionalLibrary
      ) ?? [])
    : (applyListEnforcement(
        raw.userDocCollectionIds,
        sourceControl.library.enabled,
        libraryCollectionIdsChangeable,
        effectiveEnforcedLibrary
      ) ?? []);

  let cleanIncludeUserDocs = raw.includeUserDocs;
  if (!sourceControl.library.enabled) {
    cleanIncludeUserDocs = false;
  } else if (usePerCollectionModes) {
    if ((effectiveEnforcedLibrary?.length ?? 0) > 0) {
      cleanIncludeUserDocs = true;
    } else if (cleanUserDocCollectionIds.length === 0) {
      // Optional-only with empty intersect must not send include=true + ids=[]
      // (downstream treats [] as "all collections").
      cleanIncludeUserDocs = false;
    }
  } else if (
    !libraryCollectionIdsChangeable &&
    effectiveEnforcedLibrary &&
    effectiveEnforcedLibrary.length > 0
  ) {
    // Non-negotiable pinning always forces the library on.
    cleanIncludeUserDocs = true;
  } else if (
    libraryCollectionIdsChangeable &&
    effectiveEnforcedLibrary &&
    effectiveEnforcedLibrary.length > 0 &&
    cleanUserDocCollectionIds.length === 0
  ) {
    // EVI-1368: enforcement active but nothing survived the intersect — drop the bucket so `{ ids: [], include: true }` doesn't read as "include all" downstream.
    cleanIncludeUserDocs = false;
  }

  let cleanSourceListIds = applyListEnforcement(
    raw.sourceListIds,
    sourceControl.webCollections.enabled,
    webCollectionIdsChangeable,
    legacyWebCollectionIds(enforcedWebCollectionIds).length > 0
      ? legacyWebCollectionIds(enforcedWebCollectionIds)
      : null
  );
  if (
    webCollectionIdsChangeable &&
    enforcedWebCollectionIds &&
    legacyWebCollectionIds(enforcedWebCollectionIds).length > 0 &&
    Array.isArray(cleanSourceListIds) &&
    cleanSourceListIds.length === 0
  ) {
    // EVI-1368 (web symmetry): same widening guard as the library bucket. When the enforcement intersect empties the user's web source-list IDs, send `undefined` rather than `[]` so a downstream consumer can't read `[]` as "use all source lists".
    cleanSourceListIds = undefined;
  }

  const cleanIntegrationPartners = sourceControl.integrationPartners.enabled
    ? raw.integrationPartners
    : undefined;

  const webUnifiedEnforced = unifiedWebCollectionIds(enforcedWebCollectionIds);
  const webPinned = webCollectionIdsChangeable ? [] : webUnifiedEnforced;
  const webAllowlist = webCollectionIdsChangeable ? webUnifiedEnforced : [];

  let cleanCollectionIds = raw.collectionIds;
  if (raw.collectionIds !== undefined) {
    const bucketEnabled =
      sourceControl.library.enabled || sourceControl.webCollections.enabled;

    if (!bucketEnabled) {
      cleanCollectionIds = [];
      cleanIncludeUserDocs = false;
    } else if (usePerCollectionModes) {
      cleanCollectionIds = applyUnifiedCollectionIdEnforcement(
        raw.collectionIds,
        true,
        [...(effectiveEnforcedLibrary ?? []), ...webPinned],
        effectiveOptionalLibrary ?? [],
        webAllowlist
      );
      if ((effectiveEnforcedLibrary?.length ?? 0) > 0 || webPinned.length > 0) {
        cleanIncludeUserDocs = true;
      } else if (cleanCollectionIds.length === 0) {
        cleanIncludeUserDocs = false;
      }
    } else {
      const libraryEnforced = effectiveEnforcedLibrary ?? [];
      const hasLibraryEnforcement = libraryEnforced.length > 0;
      const hasWebEnforcement = webPinned.length > 0 || webAllowlist.length > 0;
      const governedIds = new Set([...libraryEnforced, ...webUnifiedEnforced]);

      let libraryPart: string[] | undefined;
      if (hasLibraryEnforcement) {
        libraryPart = !libraryCollectionIdsChangeable
          ? [...libraryEnforced]
          : (applyListEnforcement(
              raw.collectionIds,
              true,
              true,
              libraryEnforced
            ) ?? []);
      }

      let webPart: string[] | undefined;
      if (hasWebEnforcement) {
        webPart = applyUnifiedCollectionIdEnforcement(
          raw.collectionIds,
          true,
          webPinned,
          [],
          webAllowlist
        );
      }

      const passthroughPart =
        hasWebEnforcement &&
        !hasLibraryEnforcement &&
        webCollectionIdsChangeable
          ? (raw.collectionIds ?? []).filter((id) => !governedIds.has(id))
          : hasLibraryEnforcement &&
              hasWebEnforcement &&
              (libraryCollectionIdsChangeable || webCollectionIdsChangeable)
            ? (raw.collectionIds ?? []).filter((id) => !governedIds.has(id))
            : [];

      if (hasLibraryEnforcement || hasWebEnforcement) {
        cleanCollectionIds = mergeUniqueCollectionIds(
          libraryPart,
          webPart,
          passthroughPart
        );
        cleanIncludeUserDocs =
          (!libraryCollectionIdsChangeable && libraryEnforced.length > 0) ||
          webPinned.length > 0
            ? true
            : cleanCollectionIds.length > 0
              ? raw.includeUserDocs
              : false;
      }
    }
  }

  return {
    evidenceSourceFilters: cleanFilters as EvidenceSourceFilters,
    userDocCollectionIds: cleanUserDocCollectionIds,
    sourceListIds: cleanSourceListIds,
    integrationPartners: cleanIntegrationPartners,
    includeUserDocs: cleanIncludeUserDocs,
    collectionIds: cleanCollectionIds,
  };
}
